import type { LocationQuery, RouteLocationNormalizedLoaded, RouteLocationRaw } from "vue-router";

import { articleKey, tradeAlertKey, webinarKey } from "@/types/products/product-keys";

/*
  UX roadmap #14: the content slide-over's address, ?preview=<content key>:<slug> on the page it opens over. Pure
  helpers, with no store or router instance, so a card can build its 'Read more' link from them;
  composables/useContentPreviewRoute.ts keeps the slide-over and the address in step.
*/

export const ContentPreviewQueryKey = 'preview' as const;

/** The content kinds that open in the slide-over and have a page of their own (a bonus report opens its PDF instead). */
const PREVIEW_CONTENT_KEYS: readonly string[] = [ articleKey, tradeAlertKey, webinarKey ];

export interface ContentPreviewTarget {
    contentKey: string;
    slug: string;
}

export function isPreviewContentKey( contentKey: string | undefined ): boolean {
    return !!contentKey && PREVIEW_CONTENT_KEYS.includes( contentKey );
}

/** The ?preview= value for an item: '<content key>:<slug>'. */
export function contentPreviewValue( contentKey: string, slug: string ): string {
    return `${ contentKey }:${ slug }`;
}

/** Reads a ?preview= value; anything malformed or of another kind is no preview. */
export function parseContentPreview( raw: string | undefined ): ContentPreviewTarget | null {
    const separator: number = raw?.indexOf( ':' ) ?? -1;
    if ( !raw || separator <= 0 ) return null;

    const contentKey: string = raw.slice( 0, separator );
    const slug: string = raw.slice( separator + 1 ).trim();
    return isPreviewContentKey( contentKey ) && slug ? { contentKey, slug } : null;
}

/** The current page with `value` as its preview (`undefined` drops it), keeping every other query key and the hash. */
export function withContentPreview( route: Pick< RouteLocationNormalizedLoaded, 'path' | 'query' | 'hash' >, value: string | undefined ): { path: string, query: LocationQuery, hash: string } {
    const query: LocationQuery = { ...route.query };
    if ( value ) {
        query[ ContentPreviewQueryKey ] = value;
    } else {
        delete query[ ContentPreviewQueryKey ];
    }
    return { path: route.path, query, hash: route.hash };
}

/** A link that opens an item in the slide-over over the current page: the href behind 'Read more'. */
export function contentPreviewLocation( route: Pick< RouteLocationNormalizedLoaded, 'path' | 'query' | 'hash' >, contentKey: string, slug: string ): RouteLocationRaw {
    return withContentPreview( route, contentPreviewValue( contentKey, slug ) );
}
